import { ColumnDef } from "@tanstack/react-table";
import { Download, FileCheck2, FileClock, FileText } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { createSortableHeader, DataTable } from "../../../components/ui/data-table";
import { Switch } from "../../../components/ui/switch";
import { Checkbox } from "../../../components/ui/ui/checkbox";
import { formatDateBR } from "../../../utils/format-utils";
import { formatarDocumento } from "../utils/documento";
import {
  useAtualizarFlagsNp,
  useDownloadCnabBaixa,
  useDownloadNp,
} from "../hooks/use-nota-promissoria";
import { NotaPromissoria } from "../types";
import { cnabBaixaPronto, documentosProntos, formatarCentavos, situacaoNp } from "../utils/vencimento";

interface NpTableProps {
  data: NotaPromissoria[];
  loading: boolean;
  selecionadas: number[];
  onSelecionadasChange: (ids: number[]) => void;
  onRefresh: () => void;
}

type FlagPendente = { np: NotaPromissoria; flag: "comprado" | "baixado"; valor: boolean } | null;

function SituacaoBadge({ np }: { np: NotaPromissoria }) {
  const { situacao, dias } = situacaoNp(np);
  switch (situacao) {
    case "baixada":
      return <Badge variant="secondary">Baixada</Badge>;
    case "vencida":
      return <Badge variant="destructive">Vencida há {-dias}d</Badge>;
    case "a_vencer":
      return <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">Vence em {dias}d</Badge>;
    default:
      return <Badge variant="outline">Em dia</Badge>;
  }
}

function DocIcon({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span title={`${label}: ${ok ? "gerado" : "pendente"}`} className="inline-flex items-center">
      {ok ? (
        <FileCheck2 className="h-4 w-4 text-green-600" />
      ) : (
        <FileClock className="h-4 w-4 text-muted-foreground" />
      )}
    </span>
  );
}

export function NpTable({ data, loading, selecionadas, onSelecionadasChange, onRefresh }: NpTableProps) {
  const atualizarFlags = useAtualizarFlagsNp();
  const download = useDownloadNp();
  const downloadBaixa = useDownloadCnabBaixa();
  const [flagPendente, setFlagPendente] = useState<FlagPendente>(null);

  const alternarSelecao = (id: number, marcada: boolean) =>
    onSelecionadasChange(marcada ? [...selecionadas, id] : selecionadas.filter((s) => s !== id));

  const columns: ColumnDef<NotaPromissoria>[] = [
    {
      id: "selecao",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <Checkbox
          checked={selecionadas.includes(row.original.id)}
          onCheckedChange={(v) => alternarSelecao(row.original.id, v === true)}
          aria-label={`Selecionar NP ${row.original.id_contrato}`}
        />
      ),
    },
    {
      accessorKey: "id_contrato",
      header: createSortableHeader("Contrato"),
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.id_contrato}</span>,
    },
    { accessorKey: "cedente_razao_social", header: createSortableHeader("Cedente") },
    {
      accessorKey: "nome_sacado",
      header: createSortableHeader("Sacado"),
      cell: ({ row }) => (
        <div>
          <div>{row.original.nome_sacado}</div>
          <div className="text-xs text-muted-foreground">{formatarDocumento(row.original.numero_documento_sacado)}</div>
        </div>
      ),
    },
    {
      accessorKey: "data_emissao",
      header: createSortableHeader("Emissão"),
      cell: ({ row }) => formatDateBR(row.original.data_emissao),
    },
    {
      accessorKey: "data_vencimento",
      header: createSortableHeader("Vencimento"),
      cell: ({ row }) => formatDateBR(row.original.data_vencimento),
    },
    {
      accessorKey: "valor_face",
      header: createSortableHeader("Valor Face"),
      cell: ({ row }) => formatarCentavos(row.original.valor_face),
    },
    {
      accessorKey: "valor_compra",
      header: createSortableHeader("Valor Compra"),
      cell: ({ row }) => formatarCentavos(row.original.valor_compra),
    },
    {
      id: "situacao",
      header: "Situação",
      enableSorting: false,
      cell: ({ row }) => <SituacaoBadge np={row.original} />,
    },
    {
      accessorKey: "comprado",
      header: "Comprado",
      enableSorting: false,
      cell: ({ row }) => (
        <Switch
          checked={row.original.comprado}
          disabled={atualizarFlags.isPending}
          onCheckedChange={(valor) => setFlagPendente({ np: row.original, flag: "comprado", valor })}
          aria-label="Comprado"
        />
      ),
    },
    {
      accessorKey: "baixado",
      header: "Baixado",
      enableSorting: false,
      cell: ({ row }) => (
        <Switch
          checked={row.original.baixado}
          disabled={atualizarFlags.isPending}
          onCheckedChange={(valor) => setFlagPendente({ np: row.original, flag: "baixado", valor })}
          aria-label="Baixado"
        />
      ),
    },
    {
      id: "documentos",
      header: "Docs",
      enableSorting: false,
      cell: ({ row }) => {
        const np = row.original;
        return (
          <div className="flex items-center gap-1.5">
            <DocIcon ok={!!np.cnab_remessa_key_path} label="CNAB remessa" />
            <DocIcon ok={!!np.termo_cessao_key_path} label="Termo de cessão" />
            <DocIcon ok={!!np.termo_np_key_path} label="Nota promissória" />
            <DocIcon ok={!!np.cnab_baixa_key_path} label="CNAB baixa" />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            title="Baixar ZIP (termo de cessão, NP e CNAB de remessa)"
            disabled={!documentosProntos(row.original) || download.isPending}
            onClick={() => download.mutate([row.original.id])}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            title="Baixar só o CNAB de baixa (.TXT)"
            disabled={!cnabBaixaPronto(row.original) || downloadBaixa.isPending}
            onClick={() => downloadBaixa.mutate([row.original.id])}
          >
            <FileText className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const confirmarFlag = () => {
    if (!flagPendente) return;
    atualizarFlags.mutate({ id: flagPendente.np.id, [flagPendente.flag]: flagPendente.valor });
    setFlagPendente(null);
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        title="Notas Promissórias"
        onRefresh={onRefresh}
        searchPlaceholder="Buscar por contrato, cedente, sacado..."
        searchableColumns={["id_contrato", "cedente_razao_social", "nome_sacado"]}
        exportFileName="notas-promissorias"
        pageSize={20}
      />
      <ConfirmDialog
        open={flagPendente !== null}
        onOpenChange={(open) => !open && setFlagPendente(null)}
        title={
          flagPendente
            ? `${flagPendente.valor ? "Marcar" : "Desmarcar"} NP ${flagPendente.np.id_contrato} como ${flagPendente.flag === "comprado" ? "comprada" : "baixada"}?`
            : ""
        }
        description={
          flagPendente?.flag === "baixado"
            ? "NPs baixadas saem da lista de ativas e do alerta de vencimento. Não afeta a geração do CNAB de baixa."
            : "Marca o controle da compra. O CNAB de compra é gerado junto com a NP, então a flag não muda nenhum arquivo."
        }
        onConfirm={confirmarFlag}
        confirmDisabled={atualizarFlags.isPending}
      />
    </>
  );
}
